import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain';
import { clusterConvoyByProximity } from './cluster-by-proximity';
import {
  CONVOY_SPIDERFY_RADIUS_METERS,
  CONVOY_SPIDERFY_STACK_EPSILON_METERS,
} from './constants';
import { offsetLatLngMeters } from './distance-meters';
import type { ConvoyMemberInput } from './types';

const SPIDERFY_LEG_STYLE = {
  lineColor: '#475569',
  lineWidth: 2,
  lineOpacity: 0.8,
} as const;

export type SpiderfyConvoyMembersOptions = {
  stackEpsilonMeters?: number;
  radiusMeters?: number;
};

export type SpiderfyConvoyMembersResult = {
  members: ConvoyMemberInput[];
  legs: GeoJsonFeatureCollection;
};

function emptyLegs(): GeoJsonFeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function spiderfyRadiusMeters(count: number, base: number): number {
  return base * Math.max(1, Math.sqrt(count / 2));
}

/**
 * Offset only stacked expanded pins onto a circle; already-apart pins keep true lat/lng.
 * Legs are GeoJSON LineStrings from true position to display position.
 */
export function spiderfyConvoyMembers(
  members: readonly ConvoyMemberInput[],
  options: SpiderfyConvoyMembersOptions = {},
): SpiderfyConvoyMembersResult {
  if (members.length === 0) {
    return { members: [], legs: emptyLegs() };
  }

  const epsilon =
    options.stackEpsilonMeters ?? CONVOY_SPIDERFY_STACK_EPSILON_METERS;
  const baseRadius = options.radiusMeters ?? CONVOY_SPIDERFY_RADIUS_METERS;
  const clusters = clusterConvoyByProximity(members, { radiusMeters: epsilon });

  const displayById = new Map<number, { lat: number; lng: number }>();
  const features: GeoJsonFeatureCollection['features'] = [];

  for (const cluster of clusters) {
    if (cluster.members.length < 2) {
      for (const member of cluster.members) {
        displayById.set(member.deviceId, { lat: member.lat, lng: member.lng });
      }
      continue;
    }

    const n = cluster.members.length;
    const radius = spiderfyRadiusMeters(n, baseRadius);
    const centroid = {
      lat: cluster.members.reduce((sum, m) => sum + m.lat, 0) / n,
      lng: cluster.members.reduce((sum, m) => sum + m.lng, 0) / n,
    };
    const ordered = [...cluster.members].sort(
      (a, b) => a.deviceId - b.deviceId,
    );

    for (let i = 0; i < ordered.length; i++) {
      const member = ordered[i]!;
      const angle = (2 * Math.PI * i) / n;
      const display = offsetLatLngMeters(
        centroid,
        radius * Math.sin(angle),
        radius * Math.cos(angle),
      );
      displayById.set(member.deviceId, display);
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [member.lng, member.lat],
            [display.lng, display.lat],
          ],
        },
        properties: { map: { ...SPIDERFY_LEG_STYLE } },
      });
    }
  }

  return {
    members: members.map((member) => {
      const display = displayById.get(member.deviceId);
      return display
        ? { ...member, lat: display.lat, lng: display.lng }
        : member;
    }),
    legs: { type: 'FeatureCollection', features },
  };
}
